import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AlertCircle, Camera, CheckCircle2 } from 'lucide-react';
import { assets, buildings, getAssetById } from '@/data/mockData';
import { addFault, addWorkOrder } from '@/data/faultStore';

export const FaultReportPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedAsset, setSelectedAsset] = useState('');
  const [description, setDescription] = useState('');
  const [photo, setPhoto] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredAssets = selectedBuilding
    ? assets.filter(a => a.buildingId === selectedBuilding)
    : assets;

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedAsset || !description.trim()) return;
    setIsSubmitting(true);

    const now = new Date().toISOString();
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 7);

    const wo = addWorkOrder({
      assetId: selectedAsset,
      title: description.length > 50 ? description.slice(0, 47) + '...' : description,
      description: description.trim(),
      priority: 'medium',
      status: 'open',
      assignedTo: '—',
      createdDate: now.slice(0, 10),
      dueDate: dueDate.toISOString().slice(0, 10),
      opex: 0,
      capex: 0,
      estimatedHours: 0,
      createdBy: user?.name || null,
      createdAt: now,
    });

    addFault({
      assetId: selectedAsset,
      description: description.trim(),
      reportedBy: user?.name || 'Unknown',
      reportedDate: now,
      status: 'assigned',
      workOrderId: wo.id,
      photo: photo || null,
      createdBy: user?.name || null,
      createdAt: now,
    });

    toast.success(t('faultReportSuccess'), {
      description: t('faultReportSuccessDescription'),
    });
    setSelectedBuilding('');
    setSelectedAsset('');
    setDescription('');
    setPhoto(null);
    setIsSubmitting(false);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-destructive-light rounded-xl flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-destructive" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-foreground mb-2">{t('reportFault')}</h1>
        <p className="text-muted-foreground">
          {t('reportFaultSubtitle')}
        </p>
      </div>

      {/* Form */}
      <Card className="shadow-elevated">
        <CardHeader>
          <CardTitle>{t('faultDescription')}</CardTitle>
          <CardDescription>
            {t('reportFaultCardDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Building Selection */}
            <div className="space-y-2">
              <Label htmlFor="building">{t('building')}</Label>
              <Select value={selectedBuilding} onValueChange={setSelectedBuilding}>
                <SelectTrigger id="building">
                  <SelectValue placeholder={t('selectAsset')} />
                </SelectTrigger>
                <SelectContent>
                  {buildings.map((building) => (
                    <SelectItem key={building.id} value={building.id}>
                      {building.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Asset Selection */}
            <div className="space-y-2">
              <Label htmlFor="asset">{t('equipment')}</Label>
              <Select 
                value={selectedAsset} 
                onValueChange={setSelectedAsset}
                disabled={!selectedBuilding}
              >
                <SelectTrigger id="asset">
                  <SelectValue placeholder={t('selectAsset')} />
                </SelectTrigger>
                <SelectContent>
                  {filteredAssets.map((asset) => (
                    <SelectItem key={asset.id} value={asset.id}>
                      {asset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">{t('description')}</Label>
              <Textarea
                id="description"
                placeholder={t('describeFaultPlaceholder')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                className="resize-none"
              />
            </div>

            {/* Photo Upload */}
            <div className="space-y-2">
              <Label htmlFor="photo">{t('uploadPhoto')}</Label>
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors">
                {!photo ? (
                  <label htmlFor="photo" className="cursor-pointer">
                    <input
                      id="photo"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <Camera className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-sm text-foreground font-medium mb-1">
                      {t('takeOrUploadPhoto')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('photoHint')}
                    </p>
                  </label>
                ) : (
                  <div className="space-y-4">
                    <img
                      src={photo}
                      alt="Fault photo"
                      className="max-h-64 mx-auto rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPhoto(null)}
                    >
                      {t('removePhoto')}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                className="flex-1"
                disabled={!selectedAsset || !description || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    {t('submit')}
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSelectedBuilding('');
                  setSelectedAsset('');
                  setDescription('');
                  setPhoto(null);
                }}
                disabled={isSubmitting}
              >
                {t('cancel')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
